import React, {
    ReactNode,
    useContext,
    useMemo,
    useState,
} from 'react';

import {
    QueryClient,
    QueryClientProvider,
} from 'react-query';

import { CoreContext } from '../';
import {
    AltinnClient,
    ClientError,
} from '../../api';
import { OnescoreClient } from '../../api/Onescore';
import { UpscoreClient } from '../../api/Upscore';
import {
    addUserData,
    setError,
} from '../../state';
import { StoryblokClient } from '../../storyblok';
import { TrackingService } from '../../tracking';
import { isEmptyObject } from '../../utils';
import { ApiContext } from './context';

interface Provider {
    children?: ReactNode;
}

export const ApiProvider = ({ children }: Provider) => {
    const [queryClient] = useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        refetchOnWindowFocus: false,
                    },
                },
            }),
    );
    const { settings, store } = useContext(CoreContext);

    const Upscore = useMemo(
        () =>
            new UpscoreClient(
                {
                    baseURL: settings.upscoreApiUrl[settings.env],
                    timeout: 30000,
                },
                settings.languageVersion,
            ),
        [],
    );

    const Onescore = useMemo(
        () =>
            new OnescoreClient({
                baseURL: settings.onescoreApiUrl[settings.env],
                timeout: 30000,
            }),
        [],
    );

    const Altinn = useMemo(
        () =>
            new AltinnClient({
                baseURL: settings.altinnLogin[settings.env].baseURL,
                withCredentials: true,
                headers: {
                    apiKey: settings.altinnLogin[settings.env].apiKey,
                    'Access-Control-Allow-Credentials': 'true',
                    accept: 'application/hal+json',
                },
                timeout: 30000,
            }),
        [],
    );

    const Storyblok = useMemo(
        () => new StoryblokClient(settings.languageVersion, store.dispatch),
        [],
    );

    const fetchUserScoreData = async () => {
        let response = null;
        if (settings.languageVersion === 'formue') {
            return response;
        }

        if (!response) {
            try {
                response = await Upscore.getUserScoredata();
            } catch (error: any) {
                store.dispatch(addUserData('scoreData', null));
                store.dispatch(setError('general'));

                throw new ClientError(error.message, {
                    category: 'Upscore API',
                    type: 'general',
                    error,
                });
            }
        }

        /**
         * There might be a case when user does not have scoreData and the API
         * returns just an empty object `{}`
         */
        store.dispatch(
            addUserData(
                'scoreData',
                !isEmptyObject(response.scoreData) ? response.scoreData : null,
            ),
        );

        return response;
    };

    const getRequestID = async () => {
        if (store.getState().user?.requestID) {
            return store.getState().user?.requestID;
        }

        try {
            const requestID = await Upscore.getUserRequestID().catch(
                (error) => {
                    console.error(error);
                },
            );

            store.dispatch(addUserData('requestID', requestID));

            const trackingServices = new TrackingService(
                settings.trackingServices,
            );
            trackingServices.logRocketIdentify(requestID || '');

            return requestID;
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    return (
        <QueryClientProvider client={queryClient}>
            <ApiContext.Provider
                value={{
                    Altinn,
                    Storyblok,
                    Upscore,
                    fetchUserScoreData,
                    getRequestID,
                    Onescore,
                }}
            >
                {children}
            </ApiContext.Provider>
        </QueryClientProvider>
    );
};

export * from './context';
